import React, { useState } from 'react';
import { toast } from 'sonner';
import { HelpCircle, Plus, ChevronUp, ChevronDown, Download } from 'lucide-react';

import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { QAItemForm } from '../../../components/PracticeProjects';
import { QAManagementStepProps } from '../types';

const QAManagementStep: React.FC<QAManagementStepProps> = React.memo(({
  qaItems,
  addQAItem,
  updateQAItem,
  deleteQAItem,
  reorderQAItem,
  projectId,
  projectName
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);

  const handleAddClick = () => {
    setEditingIndex(null);
    setIsDialogOpen(true);
  };

  const handleEditClick = (index: number) => {
    setEditingIndex(index);
    setIsDialogOpen(true);
  };

  const handleCSVDownload = () => {
    if (qaItems.length === 0) {
      toast.error('No questions available to download');
      return;
    }

    try {
      const headers = ["Order", "Question Text", "Variable Name", "Check Expression", "Correct Message", "Incorrect Message", "Hint Text", "Solution Text"];
      const rows = qaItems.map((q: any, i: number) => [
        i + 1,
        q.question_text || '',
        q.variable_name || '',
        q.check?.check_expression || '',
        q.check?.correct_message || '',
        q.check?.incorrect_message || '',
        q.check?.hint_text || '',
        q.check?.solution_text || ''
      ]);
      
      const csvContent = [headers, ...rows]
        .map(e => e.map(String).map(v => `"${v.replace(/"/g, '""')}"`).join(","))
        .join("\n");
        
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      
      const filename = `${(projectName || 'project').replace(/[^a-z0-9]/gi, '_').toLowerCase()}_questions.csv`;
      
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Questions exported successfully');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export questions');
    }
  };

  const handleSubmitQA = (data: {
    question_text: string;
    variable_name?: string;
    check: {
      expected_output?: any;
      check_expression?: string;
      hint_text?: string;
      solution_text?: string;
      correct_message: string;
      incorrect_message: string;
    };
    order_index: number;
  }) => {
    if (editingIndex !== null) {
      // Update existing item - use parent's updateQAItem properly
      // Store the complete item structure
      const currentItem = qaItems[editingIndex];
      const updatedItem = {
        ...currentItem,
        question_text: data.question_text,
        variable_name: data.variable_name || '',
        check: data.check,
        order_index: editingIndex
      };
      
      // Update all fields individually to trigger proper state update
      Object.keys(updatedItem).forEach(key => {
        updateQAItem(editingIndex, key, updatedItem[key]);
      });
      
      toast.success('Q&A item updated successfully');
    } else {
      // Add new item first with placeholder
      addQAItem();
      const newIndex = qaItems.length;
      
      // Then replace with actual data (use all fields to avoid keeping old ones)
      setTimeout(() => {
        const completeItem = {
          question_text: data.question_text,
          variable_name: data.variable_name || '',
          check: data.check,
          order_index: newIndex
        };
        // Replace the entire item to ensure no legacy fields remain
        Object.keys(completeItem).forEach(key => {
          updateQAItem(newIndex, key, completeItem[key as keyof typeof completeItem]);
        });
      }, 0);
      
      toast.success('Q&A item added successfully');
    }
    setIsDialogOpen(false);
  };

  const handleDeleteClick = (index: number) => {
    setItemToDelete(index);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (itemToDelete !== null) {
      deleteQAItem(itemToDelete);
      toast.success('Q&A item deleted');
      setItemToDelete(null);
    }
    setDeleteDialogOpen(false);
  };

  // Get current Q&A item for editing
  const currentQAItem = editingIndex !== null ? qaItems[editingIndex] : null;

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold">Self-Assessment Questions</h3>
            </div>
            <div className="flex gap-3">
              {qaItems.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCSVDownload}
                  className="flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download
                </Button>
              )}
              <Button
                type="button"
                onClick={handleAddClick}
                className="bg-bits-blue hover:bg-bits-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Q&A Item
              </Button>
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Add questions to help students test their understanding (check, hint, solution pattern)
          </p>

          {qaItems.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <HelpCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No Q&A items added yet</p>
              <p className="text-sm">Click "Add Q&A Item" to create self-assessment questions</p>
            </div>
          ) : (
            <div className="space-y-4">
              {qaItems.map((item: any, index: number) => (
                <Card key={index} className="border-l-4 border-l-blue-500">
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">Q{index + 1}</Badge>
                        <span className="font-medium">Question {index + 1}</span>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => reorderQAItem(index, 'up')}
                          disabled={index === 0}
                          className="h-8 w-8 p-0"
                        >
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => reorderQAItem(index, 'down')}
                          disabled={index === qaItems.length - 1}
                          className="h-8 w-8 p-0"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditClick(index)}
                          className="h-8 px-2"
                        >
                          Edit
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(index)}
                          className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div>
                        <strong className="text-gray-700">Question:</strong>
                        <p className="mt-1">{item.question_text || item.question || '(Empty)'}</p>
                      </div>
                      {item.variable_name && (
                        <div className="bg-purple-50 p-2 rounded">
                          <strong className="text-purple-700">Variable:</strong>
                          <code className="ml-2 font-mono text-xs">{item.variable_name}</code>
                        </div>
                      )}
                      {item.check?.expected_output && (
                        <div className="bg-yellow-50 p-2 rounded">
                          <strong className="text-yellow-700">Expected:</strong>
                          <code className="mt-1 block font-mono text-xs">
                            {JSON.stringify(item.check.expected_output)}
                          </code>
                        </div>
                      )}
                      {item.check?.check_expression && (
                        <div className="bg-orange-50 p-2 rounded">
                          <strong className="text-orange-700">Check:</strong>
                          <code className="mt-1 block font-mono text-xs">{item.check.check_expression}</code>
                        </div>
                      )}
                      {item.check?.hint_text && (
                        <div className="bg-blue-50 p-2 rounded">
                          <strong className="text-blue-700">Hint:</strong>
                          <p className="mt-1">{item.check.hint_text}</p>
                        </div>
                      )}
                      {item.check?.solution_text && (
                        <div className="bg-green-50 p-2 rounded">
                          <strong className="text-green-700">Solution:</strong>
                          <code className="mt-1 block font-mono text-xs">{item.check.solution_text}</code>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Q&A Dialog - Using new QAItemForm component */}
      <QAItemForm
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onSubmit={handleSubmitQA}
        qaItem={currentQAItem}
        nextOrderIndex={qaItems.length}
        isLoading={false}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Q&A Item</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this Q&A item? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
});

QAManagementStep.displayName = 'QAManagementStep';

export default QAManagementStep;
